import React, { useState } from 'react';

const DOSHAS = [
  {
    id: 'vata',
    name: 'Vāta',
    sanskrit: 'वात',
    elements: 'Air · Space',
    glyph: '🌬',
    colour: 'var(--ivory-d)',
    accent: '#9BB5C0',
    tagline: 'The force of movement',
    desc: 'Vata governs all motion in the body — breath, circulation, nerve impulses, and the flow of thought. When in balance it brings creativity, vitality, and adaptability.',
    qualities: ['Dry', 'Light', 'Cold', 'Subtle', 'Mobile'],
    signs: [
      'Dry or rough skin',
      'Anxiety & restlessness',
      'Irregular digestion',
      'Joint stiffness',
      'Disturbed sleep',
    ],
    balancedWith: 'Warm oils · Grounding routines · Abhyanga massage',
  },
  {
    id: 'pitta',
    name: 'Pitta',
    sanskrit: 'पित्त',
    elements: 'Fire · Water',
    glyph: '🔥',
    colour: 'var(--gold)',
    accent: '#C9963A',
    tagline: 'The force of transformation',
    desc: 'Pitta drives digestion, metabolism, intelligence, and transformation at every level — from food to ideas. In balance it brings sharp focus, warmth, and courage.',
    qualities: ['Hot', 'Sharp', 'Light', 'Oily', 'Spreading'],
    signs: [
      'Skin inflammation & rashes',
      'Acid reflux',
      'Irritability & impatience',
      'Excess body heat',
      'Premature greying',
    ],
    balancedWith: 'Cooling herbs · Shirodhara · Pitta-pacifying diet',
  },
  {
    id: 'kapha',
    name: 'Kapha',
    sanskrit: 'कफ',
    elements: 'Earth · Water',
    glyph: '🌿',
    colour: 'var(--sage-l)',
    accent: '#5C8050',
    tagline: 'The force of cohesion',
    desc: 'Kapha provides structure, lubrication, and immunity — it holds the body together and sustains its tissues. In balance it brings strength, calm, and compassion.',
    qualities: ['Heavy', 'Slow', 'Cool', 'Oily', 'Smooth'],
    signs: [
      'Weight gain & sluggishness',
      'Nasal congestion',
      'Excess mucus',
      'Lethargy & low motivation',
      'Oily skin & pores',
    ],
    balancedWith: 'Udvartana · Stimulating therapies · Light, warm diet',
  },
];

export default function DoshaSection() {
  const [active, setActive] = useState('pitta');
  const dosha = DOSHAS.find((d) => d.id === active);

  return (
    <section className="dosha-section" id="doshas">
      <div className="container">
        <div className="dosha-header fade-up">
          <p className="section-label">Know Your Constitution</p>
          <h2 className="section-title">The Three <em>Doshas</em></h2>
          <p className="dosha-intro">
            Ayurveda recognises three fundamental bio-energies — Vata, Pitta, and Kapha —
            derived from the five elements. Your unique combination, or <em>Prakriti</em>,
            determines your physical tendencies, emotional patterns, and the care that
            restores you to balance.
          </p>
        </div>

        {/* Tab selector */}
        <div className="dosha-tabs fade-up">
          {DOSHAS.map((d) => (
            <button
              key={d.id}
              className={`dosha-tab ${active === d.id ? 'active' : ''}`}
              onClick={() => setActive(d.id)}
              style={active === d.id ? { '--tab-accent': d.accent } : {}}
            >
              <span className="dosha-tab-glyph">{d.glyph}</span>
              <span className="dosha-tab-name">{d.name}</span>
              <span className="dosha-tab-elem">{d.elements}</span>
            </button>
          ))}
        </div>

        {/* Active dosha panel */}
        <div className="dosha-panel fade-up">
          <div className="dosha-panel-left">
            <div className="dosha-glyph-wrap" style={{ '--tab-accent': dosha.accent }}>
              <span className="dosha-big-glyph">{dosha.glyph}</span>
              <div className="dosha-sanskrit">{dosha.sanskrit}</div>
            </div>
            <h3 className="dosha-name" style={{ color: dosha.accent }}>{dosha.name}</h3>
            <p className="dosha-tagline">{dosha.tagline}</p>
            <div className="dosha-qualities">
              {dosha.qualities.map((q) => (
                <span key={q} className="dosha-quality-tag" style={{ borderColor: dosha.accent + '44', color: dosha.accent }}>
                  {q}
                </span>
              ))}
            </div>
          </div>
          <div className="dosha-panel-right">
            <p className="dosha-desc">{dosha.desc}</p>
            <div className="dosha-two-col">
              <div className="dosha-signs-block">
                <h4 className="dosha-block-title">Imbalance Signs</h4>
                <ul className="dosha-signs">
                  {dosha.signs.map((s) => <li key={s}>{s}</li>)}
                </ul>
              </div>
              <div className="dosha-balance-block">
                <h4 className="dosha-block-title">Restore Balance</h4>
                <p className="dosha-balance-text">{dosha.balancedWith}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
